import { useLoginPage, usePasskeyLogin } from "@app/auth/hooks";
import { trans } from "@mongez/localization";
import { useLocation } from "@remix-run/react";
import {
  FaArrowRight,
  FaFingerprint,
  FaQrcode,
  FaTelegram,
} from "react-icons/fa";
import { LoginForm, QrLoginForm } from "../components";
import { TelegramLoginForm } from "../components/TelegramLoginForm";
import { getHasImpersonationError } from "./has-impersonation-error.util";

export function LoginPageContent() {
  const loginPage = useLoginPage();
  const passkey = usePasskeyLogin();
  const location = useLocation();
  const hasImpersonationError = getHasImpersonationError(location.search);
  const wasSuspended = new URLSearchParams(location.search).has("suspended");

  if (loginPage.mode !== "password") {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={loginPage.showPasswordLogin}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            <FaArrowRight aria-hidden />
            {trans("auth.backToLogin")}
          </button>
        </div>

        {loginPage.mode === "qr" ? <QrLoginForm /> : <TelegramLoginForm />}
      </div>
    );
  }

  const hasAlternativeLogin =
    loginPage.isTelegramEnabled ||
    loginPage.isQrLoginEnabled ||
    loginPage.isPasskeyLoginEnabled;

  return (
    <div className="space-y-6">
      {wasSuspended && (
        <p
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
          role="alert"
        >
          {trans("auth.suspended")}
        </p>
      )}
      {hasImpersonationError && (
        <p
          className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
          role="alert"
        >
          {trans("auth.impersonationFailed")}
        </p>
      )}
      <LoginForm />

      {hasAlternativeLogin ? (
        <>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                {trans("auth.or")}
              </span>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {loginPage.isPasskeyLoginEnabled ? (
              <button
                type="button"
                disabled={passkey.loading}
                onClick={passkey.login}
                className="flex w-full items-center justify-center gap-3 rounded-xl border border-violet-300 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700 transition-colors duration-200 hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60 dark:border-violet-800 dark:bg-violet-950/50 dark:text-violet-200"
              >
                <FaFingerprint size={20} />
                {passkey.loading
                  ? trans("auth.passkeySigningIn")
                  : trans("auth.loginWithPasskey")}
              </button>
            ) : null}

            {loginPage.isQrLoginEnabled ? (
              <button
                type="button"
                onClick={loginPage.showQrLogin}
                className="flex w-full items-center justify-center gap-3 rounded-xl border border-primary/25 bg-primary/5 px-4 py-3 text-sm font-semibold text-primary transition-colors duration-200 hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <FaQrcode size={20} />
                {trans("auth.loginWithQr")}
              </button>
            ) : null}

            {loginPage.isTelegramEnabled ? (
              <button
                type="button"
                onClick={loginPage.showTelegramLogin}
                className="flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <FaTelegram className="text-blue-500" size={20} />
                {trans("auth.loginWithTelegram")}
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
